import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const CustomDropdown = ({
  options = [],
  value,
  onChange,
  placeholder,
  icon: Icon,
  align = 'left',
  className = '',
  solid = false,
  backgroundColor = null,
  showHeader = true,
  disabled = false,
  menuWidthPx = null,
  searchable = false
}) => {

  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const displayPlaceholder = placeholder || t('selectOption');
  const selectedOption = options.find(opt => opt.value === value);
  const currentActiveColor = (selectedOption && selectedOption.activeColor) || backgroundColor;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen((v) => !v);
  };

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      if (searchable) {
        // Focus after the menu mounts
        setTimeout(() => searchRef.current?.focus?.(), 0);
      }
    }
  }, [isOpen, searchable]);

  const normalizedQuery = (query || '').trim().toLowerCase();
  const filteredOptions = !searchable || !normalizedQuery
    ? options
    : options.filter((opt) => {
      const label = (opt.label || '').toString().toLowerCase();
      const sub = (opt.subLabel || '').toString().toLowerCase();
      return label.includes(normalizedQuery) || sub.includes(normalizedQuery);
    });

  return (
    <div
      className={`relative ${className}`}
      ref={dropdownRef}
      style={{ zIndex: isOpen ? 99999 : 10, maxWidth: menuWidthPx ? `${menuWidthPx / 16}rem` : undefined }}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={toggleDropdown}
        className={`flex items-center gap-2 border rounded-xl px-4 py-2.5 transition-all w-full text-left ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'} ${solid ? 'text-white border-white/20' : 'bg-white/5 text-slate-200 hover:bg-white/10 hover:border-white/20'
          }`}

        style={{
          position: 'relative',
          zIndex: 1,
          borderColor: !solid && currentActiveColor ? `${currentActiveColor}40` : (solid ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)'),
          backgroundColor: solid && currentActiveColor ? currentActiveColor : undefined
        }}
      >
        {Icon && <Icon size={16} style={{ color: !solid && currentActiveColor ? currentActiveColor : (solid ? '#ffffff' : undefined) }} className={!currentActiveColor && !solid ? "text-primary" : ""} />}
        <div className="flex-1 flex flex-col min-w-0 pr-2">
          <span className={`text-sm font-bold truncate ${solid ? 'text-white' : ''}`}>
            {selectedOption ? selectedOption.label : displayPlaceholder}
          </span>
          {selectedOption?.subLabel && (
            <span className={`text-[10px] opacity-60 truncate ${solid ? 'text-white/90' : 'text-slate-500'}`}>
              {selectedOption.subLabel}
            </span>
          )}
        </div>
        <ChevronDown size={16} className={`${solid ? 'text-white' : 'text-slate-500'} transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className={`absolute top-full left-0 mt-2 z-[99999] border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.8)] rounded-2xl overflow-hidden ${align === 'right' ? 'right-0 left-auto' : 'left-0'
            } ${solid ? '' : 'bg-slate-900'}`}
          style={{
            ...(solid && currentActiveColor ? { backgroundColor: currentActiveColor, backgroundImage: 'none' } : { backgroundColor: '#0f172a' }),
            width: '100%',
          }}
        >
          {showHeader && (
            <div className="p-2 border-b border-white/5 bg-black/20">
              <span className={`text-[10px] font-black uppercase tracking-widest px-2 ${solid ? 'text-white/60' : 'text-slate-500'}`}>{displayPlaceholder}</span>
            </div>
          )}
          {searchable && (
            <div className="p-2 border-b border-white/5">
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('search')}
                className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm text-slate-200 placeholder:text-slate-600 focus:border-primary/30"
              />
            </div>
          )}
          <div className="max-h-60 overflow-y-auto custom-scrollbar p-1.5 space-y-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs font-bold text-slate-500">
                {t('noResultsFound')}
              </div>
            ) : filteredOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[11px] font-bold transition-all group/opt ${value === option.value
                  ? 'text-white ring-1 ring-white/20'
                  : (solid ? 'text-white/70 hover:bg-white/10 hover:text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white')
                  }`}
                style={value === option.value ? {
                  backgroundColor: option.activeColor || '#3b82f6',
                } : {}}
              >
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  {option.icon && (
                    <option.icon
                      size={14}
                      className={value === option.value ? 'text-white' : (solid ? 'text-white/50 group-hover/opt:text-white' : 'text-slate-500 group-hover/opt:text-white')}
                    />
                  )}
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{option.label}</span>
                    {option.subLabel && (
                      <span className={`text-[9px] opacity-60 truncate ${value === option.value ? 'text-white' : (solid ? 'text-white/50' : 'text-slate-500')}`}>
                        {option.subLabel}
                      </span>
                    )}
                  </div>
                </div>
                {value === option.value && <Check size={14} className="flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;
